import { FunctionalComponent } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { Search, Link as LinkIcon, Loader2, Building2, ExternalLink } from 'lucide-preact'
import { companiesApi, type CompanySearchResultDto, type CompanyEnrichedDto } from '@/api/companies'
import type { LookupOptionDto } from '@/types/lookups'
import { handleApiError } from '@/utils/errorHandling'
import { useToast } from '@/hooks/useToast'
import { useDebouncedValue } from '@/hooks'
import { useLookups } from '@/hooks/useLookups'
import type { UseFormRegister, FieldErrors, UseFormTrigger } from 'react-hook-form'

interface CompanyEditSectionProps {
  companyLinkedInId: string
  companyName?: string | null
  industry?: string | null
  companySize?: string | null
  companyWebsite?: string | null
  originalCompanyLinkedInId?: string
  isDark: boolean
  saving: boolean
  errors: FieldErrors<{ CompanyLinkedInId: string; CompanyName?: string; Industry?: string; CompanySize?: string; CompanyWebsite?: string }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger: UseFormTrigger<any>
  onChange: (linkedInId: string, companyName: string, industry?: string, companySize?: string, companyWebsite?: string) => void
  onLoadingChange?: (loading: boolean) => void
}

interface InfoRowProps {
  icon: preact.ComponentChildren
  label: string
  value?: string | null
  link?: string
  dark?: boolean
}

const InfoRow: FunctionalComponent<InfoRowProps> = ({ icon, label, value, link, dark }) => {
  const display = value || '—'
  return (
    <div class="flex items-start justify-between gap-4">
      <div class="flex items-center gap-2 min-w-0">
        <span class={dark ? 'text-slate-400' : 'text-slate-500'} aria-hidden="true">{icon}</span>
        <span class={`text-[11px] uppercase tracking-wide font-semibold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
      </div>
      <div class="text-right flex-1 min-w-0">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            class={`inline-flex items-center gap-1 hover:underline text-sm truncate max-w-[320px] ${dark ? 'text-primary-400' : 'text-primary-600'}`}
            onClick={e => e.stopPropagation()}
          >{display} <ExternalLink class="w-3.5 h-3.5" /></a>
        ) : (
          <span class={`text-sm truncate max-w-[320px] ${dark ? 'text-slate-200' : 'text-slate-700'}`} title={display}>{display}</span>
        )}
      </div>
    </div>
  )
}

export const CompanyEditSection: FunctionalComponent<CompanyEditSectionProps> = ({
  companyLinkedInId,
  companyName,
  industry,
  companySize,
  companyWebsite,
  originalCompanyLinkedInId,
  isDark,
  saving,
  errors,
  register,
  trigger,
  onChange,
  onLoadingChange
}) => {
  const { showError } = useToast()
  const lookups = useLookups()
  const [companyQuery, setCompanyQuery] = useState('')
  const debouncedCompanyQuery = useDebouncedValue(companyQuery, 300)
  const [companyResults, setCompanyResults] = useState<CompanySearchResultDto[]>([])
  const [companyLoading, setCompanyLoading] = useState(false)
  const [enrichLoading, setEnrichLoading] = useState(false)
  // Auto-detect override mode: enabled if there's a company name but no LinkedIn ID
  const [useOverride, setUseOverride] = useState(!companyLinkedInId && !!companyName)
  const abortRef = useRef<AbortController | null>(null)
  const companySearchContainerRef = useRef<HTMLDivElement | null>(null)

  // Get cached company size ranges from hook
  const companySizeRanges: LookupOptionDto[] = lookups.getCompanySizeRanges().map(x => ({ Id: x.Key, DisplayName: x.Description }))

  useEffect(() => {
    if (debouncedCompanyQuery.length < 3) { setCompanyResults([]); return }
    // Cancel prior request
    if (abortRef.current) { abortRef.current.abort() }
    const controller = new AbortController()
    abortRef.current = controller
    setCompanyLoading(true)
    companiesApi.search(debouncedCompanyQuery, 25, controller.signal)
      .then(resp => {
        if (!controller.signal.aborted) {
          setCompanyResults(resp.data || [])
        }
      })
      .catch(err => {
        if (err?.name !== 'AbortError') {
          console.error('Company search error', err)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setCompanyLoading(false)
      })
    // Cleanup: abort request if component unmounts or dependencies change
    return () => {
      controller.abort()
    }
  }, [debouncedCompanyQuery])

  // Click-outside handler to hide results
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!companySearchContainerRef.current) return
      if (!companySearchContainerRef.current.contains(e.target as Node)) {
        setCompanyResults([])
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  async function enrichById() {
    if (!companyLinkedInId) return
    setEnrichLoading(true)
    onLoadingChange?.(true)
    try {
      const resp = await companiesApi.enrichByLinkedInId(companyLinkedInId)
      if (resp.error) {
        handleApiError(resp, showError, () => { }, 'Fetch Failed')
        return
      }
      const company = resp.data as CompanyEnrichedDto | undefined
      if (company) {
        const newName = company.Name || company.NormalizedName || companyName || ''
        onChange(companyLinkedInId, newName, undefined, undefined, companyWebsite || '')
      }
    } catch (e) {
      handleApiError(e, showError, () => { }, 'Fetch Failed')
    } finally {
      setEnrichLoading(false)
      onLoadingChange?.(false)
    }
  }

  function handleCompanySelect(result: CompanySearchResultDto) {
    const linkedInId = (result.Id || '').toString().trim()
    const name = result.Name?.trim() || result.NormalizedName?.trim() || linkedInId
    onChange(linkedInId, name, undefined, undefined, companyWebsite || '')
    setCompanyQuery('')
    setCompanyResults([])
    setUseOverride(false)
  }

  function handleOverrideModeToggle() {
    const newOverrideState = !useOverride
    setUseOverride(newOverrideState)
    if (newOverrideState) {
      // Clear LinkedIn ID when switching to override mode
      onChange('', companyName || '', industry || '', companySize || '', companyWebsite || '')
    } else {
      // Clear all fields when switching back to LinkedIn mode to prevent data integrity issues
      // (e.g., having a LinkedIn ID that doesn't match a manually entered company name)
      onChange('', '', '', '', '')
      // Trigger validation on CompanyLinkedInId to reflect the new mode requirements
      setTimeout(() => trigger('CompanyLinkedInId'), 0)
    }
  }

  return (
    <div class="flex flex-col gap-3">
      {/* Helper text explaining the workflow */}
      <div class={`text-[11px] px-3 py-2 rounded-md ${isDark ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
        💡 Search companies in your database, enter a LinkedIn ID, or override with free-form company data.
      </div>

      {/* Override mode toggle */}
      <div class="flex items-center gap-2">
        <input
          type="checkbox"
          id="override-mode"
          checked={useOverride}
          onChange={handleOverrideModeToggle}
          class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="override-mode" class={`text-sm font-medium cursor-pointer ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
          Override with free-form company data (no LinkedIn)
        </label>
      </div>

      {!useOverride ? (
        <>
          {/* LinkedIn Company Mode */}
          <div class="flex flex-col gap-1" ref={companySearchContainerRef}>
            <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Search Your Companies</label>
            <div class="relative">
              <Search class="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={companyQuery}
                onInput={e => setCompanyQuery((e.target as HTMLInputElement).value)}
                placeholder="Search companies in your database (min 3 chars)"
                class={`w-full pl-8 pr-3 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
              />
            </div>
            {companyLoading && (
              <div class="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <Loader2 class="w-3 h-3 animate-spin" /> Searching...
              </div>
            )}
            {!companyLoading && companyResults.length > 0 && (
              <ul class={`max-h-44 overflow-auto rounded-md divide-y bg-white dark:bg-slate-800 ${isDark ? 'border border-slate-600 divide-slate-600' : 'border border-slate-200 divide-slate-200'}`}>
                {companyResults.map(r => {
                  const displayId = r.Id
                  return (
                    <li
                      key={r.Id}
                      class={`p-2 cursor-pointer text-xs flex items-center gap-2 ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}
                      onClick={() => handleCompanySelect(r)}
                    >
                      {r.Logo && <img src={r.Logo} alt="logo" class="w-5 h-5 rounded object-cover" />}
                      <span class={`font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                        {r.Name || r.NormalizedName || displayId || 'Company'}
                      </span>
                      {displayId && (
                        <span class={`text-[10px] px-1 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                          {displayId}
                        </span>
                      )}
                      {r.StaffCountRange && (
                        <span class={`text-[10px] ml-auto ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {r.StaffCountRange}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div class="grid grid-cols-3 gap-2 items-end">
            <div class="col-span-2 flex flex-col gap-1">
              <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Or Enter LinkedIn Company ID or URL
              </label>
              <input
                value={companyLinkedInId || ''}
                maxLength={2083}
                {...register('CompanyLinkedInId', {
                  validate: v => {
                    // Allow empty if in override mode
                    if (useOverride) return true
                    // If company name is set, we have a valid company selection
                    if (companyName && companyName.trim().length > 0) return true
                    // Otherwise, CompanyLinkedInId must not be blank
                    if (!v || v.trim().length === 0) return 'LinkedIn Company ID or URL cannot be blank.'

                    const trimmed = v.trim()
                    if (trimmed.length > 2083) return 'Input exceeds maximum length of 2083 characters.'

                    // If it looks like a URL, validate it contains linkedin.com/company/
                    if (trimmed.includes('/') || trimmed.startsWith('http')) {
                      if (!trimmed.includes('linkedin.com/company/')) {
                        return 'Invalid LinkedIn URL. Must be a LinkedIn company URL.'
                      }
                    }
                    // If it's just digits, that's a valid ID
                    else if (!/^\d+$/.test(trimmed)) {
                      return 'Must be a LinkedIn company ID (number) or LinkedIn company URL.'
                    }

                    return true
                  },
                  onChange: e => onChange((e.target as HTMLInputElement).value, companyName || '', industry || '', companySize || '', companyWebsite || '')
                })}
                placeholder="e.g. 123456 or https://linkedin.com/company/orbital-x"
                class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.CompanyLinkedInId ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
              />
              {errors.CompanyLinkedInId && (
                <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  {errors.CompanyLinkedInId.message}
                </span>
              )}
            </div>
            <div class="flex flex-col gap-1 justify-end">
              <button
                type="button"
                disabled={saving || !companyLinkedInId || enrichLoading || companyLinkedInId.trim() === (originalCompanyLinkedInId || '').trim()}
                onClick={enrichById}
                class="inline-flex items-center justify-center gap-1 px-2.5 py-2 rounded-md text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                title={companyLinkedInId && companyLinkedInId.trim() === (originalCompanyLinkedInId || '').trim() ? 'Enter a new LinkedIn Company ID to fetch' : 'Fetch company data from LinkedIn'}
              >
                {enrichLoading ? <Loader2 class="w-3.5 h-3.5 animate-spin" /> : <LinkIcon class="w-3.5 h-3.5" />} Fetch
              </button>
            </div>
          </div>

          {/* Company Name label-style field (read-only, updates with selection) */}
          <InfoRow
            icon={<Building2 class="w-4 h-4" />}
            label="Company"
            value={companyName}
            link={companyLinkedInId ? `https://www.linkedin.com/company/${companyLinkedInId}` : undefined}
            dark={isDark}
          />

          {/* Company Website - editable in LinkedIn mode */}
          <div class="flex flex-col gap-1">
            <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Company Website
            </label>
            <input
              value={companyWebsite || ''}
              maxLength={500}
              {...register('CompanyWebsite', {
                maxLength: { value: 500, message: 'Company Website must be 500 characters or fewer.' },
                onChange: e => onChange(companyLinkedInId || '', companyName || '', industry || '', companySize || '', (e.target as HTMLInputElement).value)
              })}
              placeholder="https://example.com"
              class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.CompanyWebsite ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
            />
            {errors.CompanyWebsite && (
              <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {errors.CompanyWebsite.message}
              </span>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Override Mode - Free-form company data */}
          <div class="flex flex-col gap-1">
            <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Company Name
            </label>
            <input
              value={companyName || ''}
              maxLength={200}
              {...register('CompanyName', {
                validate: v => {
                  if (!useOverride) return true
                  if (!v || v.trim().length === 0) return 'Company Name cannot be blank in override mode.'
                  if (v.trim().length > 200) return 'Company Name must be 200 characters or fewer.'
                  return true
                },
                onChange: e => onChange('', (e.target as HTMLInputElement).value, industry || '', companySize || '', companyWebsite || '')
              })}
              placeholder="Enter company name"
              class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.CompanyName ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
            />
            {errors.CompanyName && (
              <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {errors.CompanyName.message}
              </span>
            )}
          </div>

          <div class="flex flex-col gap-1">
            <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Company Website
            </label>
            <input
              value={companyWebsite || ''}
              maxLength={500}
              {...register('CompanyWebsite', {
                maxLength: { value: 500, message: 'Company Website must be 500 characters or fewer.' },
                onChange: e => onChange('', companyName || '', industry || '', companySize || '', (e.target as HTMLInputElement).value)
              })}
              placeholder="https://example.com"
              class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.CompanyWebsite ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
            />
            {errors.CompanyWebsite && (
              <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {errors.CompanyWebsite.message}
              </span>
            )}
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-1">
              <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Industry
              </label>
              <input
                value={industry || ''}
                maxLength={100}
                {...register('Industry', {
                  maxLength: { value: 100, message: 'Industry must be 100 characters or fewer.' },
                  onChange: e => onChange('', companyName || '', (e.target as HTMLInputElement).value, companySize || '', companyWebsite || '')
                })}
                placeholder="e.g. Technology"
                class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.Industry ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
              />
              {errors.Industry && (
                <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  {errors.Industry.message}
                </span>
              )}
            </div>

            <div class="flex flex-col gap-1">
              <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Company Size
              </label>
              <select
                value={companySize || ''}
                {...register('CompanySize', {
                  onChange: e => onChange('', companyName || '', industry || '', (e.target as HTMLSelectElement).value, companyWebsite || '')
                })}
                class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.CompanySize ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100' : 'border-slate-300 bg-white text-slate-900'}`}
              >
                <option value="">Select size...</option>
                {companySizeRanges.map(range => (
                  <option key={range.Id} value={range.DisplayName}>
                    {range.DisplayName}
                  </option>
                ))}
              </select>
              {errors.CompanySize && (
                <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  {errors.CompanySize.message}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CompanyEditSection
